import React, { useState } from 'react';
import styled from 'styled-components';
import { Icon } from '~/components';
import { colors, weights } from '~/styles';
import { JsonBlock } from './shared';
import { computeSpanCost, formatCost } from './formatUtils';

const Block = styled.div`
  border: solid 1px ${colors.grey10};
  border-radius: 0.25rem;
  margin: 0.5rem 0;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  cursor: pointer;
  font-size: 0.8125rem;

  &:hover {
    background: ${colors.grey5};
  }
`;

const Chevron = styled.span`
  font-size: 0.625rem;
  color: ${colors.grey40};
  width: 0.75rem;
`;

const StepLabel = styled.span`
  font-weight: ${weights.bold};
  color: ${colors.black};
`;

const Meta = styled.span`
  color: ${colors.grey55};
  font-size: 0.75rem;
`;

const Badge = styled.span`
  display: inline-block;
  padding: 0.125rem 0.375rem;
  border-radius: 0.125rem;
  font-size: 0.6875rem;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', monospace;
  background: ${colors.grey5};
  color: ${colors.grey55};
`;

const Body = styled.div`
  padding: 0.75rem;
  border-top: solid 1px ${colors.grey10};
`;

const SectionHeader = styled.div`
  font-size: 0.75rem;
  font-weight: ${weights.bold};
  color: ${colors.grey55};
  text-transform: uppercase;
  margin-top: 1rem;
  margin-bottom: 0.5rem;

  &:first-child {
    margin-top: 0;
  }
`;

const ToolResult = styled.div`
  margin-bottom: 0.75rem;
  padding: 0.5rem 0.75rem;
  border: solid 1px ${colors.grey10};
  border-radius: 0.25rem;

  &.nested {
    margin-left: 1.25rem;
  }
`;

const ToolHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
`;

const ToolResultIcon = styled(Icon)`
  font-size: 0.625rem;
`;

const ToolNameLabel = styled.span`
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', monospace;
  font-size: 0.75rem;
  font-weight: ${weights.bold};
  color: ${colors.black};
`;

const ErrorText = styled.div`
  color: ${colors.danger};
  font-size: 0.8125rem;
  margin-top: 0.25rem;
`;

const ProgramStatus = styled.span`
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', monospace;
  font-size: 0.6875rem;
  border-radius: 0.25rem;
  padding: 0.0625rem 0.25rem;
  margin-left: 0.375rem;
  background: ${({ $ok }) => ($ok ? colors.success10 : colors.danger5)};
  color: ${({ $ok }) => ($ok ? colors.success : colors.danger)};
`;

const CallerTag = styled.span`
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', monospace;
  font-size: 0.6875rem;
  color: ${colors.grey55};
  background: ${colors.grey5};
  border-radius: 0.25rem;
  padding: 0.0625rem 0.25rem;
`;

const ProgramCode = styled.pre`
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', monospace;
  font-size: 0.75rem;
  line-height: 1.5;
  color: ${colors.black};
  background: ${colors.grey5};
  border: solid 1px ${colors.grey10};
  border-radius: 0.25rem;
  padding: 0.5rem 0.75rem;
  margin: 0 0 0.75rem 0;
  overflow-x: auto;
  white-space: pre;
`;

const UsageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(8rem, 1fr));
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const UsageItem = styled.div`
  font-size: 0.75rem;
  color: ${colors.grey55};
`;

const UsageValue = styled.span`
  font-weight: ${weights.bold};
  color: ${colors.black};
`;

const JsonLabel = styled.div`
  font-size: 0.75rem;
  color: ${colors.grey55};
  margin-bottom: 0.25rem;
`;

const ToolListBlock = styled.div`
  margin-bottom: 0.5rem;
`;

const ToolListNames = styled.div`
  font-size: 0.75rem;
  font-family: monospace;
  color: ${colors.grey55};
`;

const RecoveryItem = styled.div`
  font-size: 0.8125rem;
  color: ${colors.grey55};
  margin-bottom: 0.25rem;
`;

const ExpandToggle = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: ${colors.primary};
  cursor: pointer;
  height: auto;
  border-radius: 0;
  font-weight: ${weights.normal};

  &:hover {
    color: ${colors.accent};
    background: none;
  }
`;

function formatTokens(n) {
  if (n == null) return '-';
  return n.toLocaleString();
}

function formatDuration(ms) {
  if (ms == null) return '-';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function CollapsibleJson({ data, label }) {
  const [expanded, setExpanded] = useState(false);

  if (!data) return null;

  const preview = JSON.stringify(data).slice(0, 200);
  const full = JSON.stringify(data, null, 2);
  const isLong = full.length > 200;

  return (
    <div>
      {label && <JsonLabel>{label}</JsonLabel>}
      <JsonBlock>{expanded ? full : preview + (isLong ? '...' : '')}</JsonBlock>
      {isLong && <ExpandToggle onClick={() => setExpanded(!expanded)}>{expanded ? 'Collapse' : 'Expand'}</ExpandToggle>}
    </div>
  );
}

export default function SpanBlock({ span, index, pricing }) {
  const [expanded, setExpanded] = useState(false);

  const model = span.request?.eventData?.model || span.response?.eventData?.model;
  const usage = span.response?.eventData?.usage;
  const inputTokens = usage?.input_tokens;
  const outputTokens = usage?.output_tokens;
  const cost = computeSpanCost(usage, pricing?.[model]);

  const programOutcomes = new Map(
    span.programOutputs?.map((event) => [event.eventData?.call_id, event.eventData]) ?? [],
  );

  // A program-initiated result renders under its program; everything else in the flat list.
  const toolOutputsByProgram = new Map();
  const directToolOutputs = [];
  for (const output of span.toolOutputs) {
    const programCallId = output.eventData?.caller?.caller_id;
    if (!programCallId) {
      directToolOutputs.push(output);
      continue;
    }
    if (!toolOutputsByProgram.has(programCallId)) toolOutputsByProgram.set(programCallId, []);
    toolOutputsByProgram.get(programCallId).push(output);
  }
  // An orphan — its program's event is missing — stays visible in the flat list with its caller tag
  // rather than vanishing into a group that never renders.
  for (const [programCallId, outputs] of toolOutputsByProgram) {
    if (span.programs.some((p) => p.eventData?.call_id === programCallId)) continue;
    directToolOutputs.push(...outputs);
    toolOutputsByProgram.delete(programCallId);
  }

  return (
    <Block>
      <Header onClick={() => setExpanded(!expanded)}>
        <Chevron>
          <Icon icon={expanded ? 'chevron-down' : 'chevron-right'} />
        </Chevron>
        <StepLabel>{span.label || `Step ${index + 1}`}</StepLabel>
        {model && <Badge>{model}</Badge>}
        {inputTokens != null && (
          <Meta>
            {formatTokens(inputTokens)} in / {formatTokens(outputTokens)} out
          </Meta>
        )}
        {cost && <Meta>{formatCost(cost.totalCost)}</Meta>}
        <Meta>{formatDuration(span.durationMs)}</Meta>
      </Header>

      {expanded && (
        <Body>
          {span.request && (
            <>
              <SectionHeader>Request</SectionHeader>
              {span.request.eventData?.tools && (
                <ToolListBlock>
                  <JsonLabel>Tools ({span.request.eventData.tools.length})</JsonLabel>
                  <ToolListNames>
                    {span.request.eventData.tools
                      .map((t) => t.name || t.function?.name)
                      .filter(Boolean)
                      .join(', ')}
                  </ToolListNames>
                </ToolListBlock>
              )}
              <CollapsibleJson data={span.request.eventData?.input} label="Input" />
            </>
          )}

          {span.response && (
            <>
              <SectionHeader>Response</SectionHeader>
              {usage && (
                <UsageGrid>
                  <UsageItem>
                    Input: <UsageValue>{formatTokens(usage.input_tokens)}</UsageValue>
                  </UsageItem>
                  <UsageItem>
                    Output: <UsageValue>{formatTokens(usage.output_tokens)}</UsageValue>
                  </UsageItem>
                  {usage.input_tokens_details?.cached_tokens != null && (
                    <UsageItem>
                      Cached: <UsageValue>{formatTokens(usage.input_tokens_details.cached_tokens)}</UsageValue>
                    </UsageItem>
                  )}
                  {cost && (
                    <>
                      <UsageItem>
                        Input cost: <UsageValue>{formatCost(cost.inputCost)}</UsageValue>
                      </UsageItem>
                      {cost.cachedInputCost > 0 && (
                        <UsageItem>
                          Cached cost: <UsageValue>{formatCost(cost.cachedInputCost)}</UsageValue>
                        </UsageItem>
                      )}
                      <UsageItem>
                        Output cost: <UsageValue>{formatCost(cost.outputCost)}</UsageValue>
                      </UsageItem>
                      <UsageItem>
                        Total cost: <UsageValue>{formatCost(cost.totalCost)}</UsageValue>
                      </UsageItem>
                    </>
                  )}
                </UsageGrid>
              )}
              <CollapsibleJson data={span.response.eventData?.output} label="Output" />
              {span.response.eventData?.reasoningSummary?.length > 0 && (
                <>
                  <JsonLabel>Reasoning summary</JsonLabel>
                  {span.response.eventData.reasoningSummary.map((part, i) => (
                    <ProgramCode key={i}>{part}</ProgramCode>
                  ))}
                </>
              )}
            </>
          )}

          {span.programs.length > 0 && (
            <>
              <SectionHeader>Programs</SectionHeader>
              {span.programs.map((p, i) => (
                <div key={p.eventData?.call_id || i}>
                  <JsonLabel>
                    {p.eventData?.call_id}
                    {programOutcomes.get(p.eventData?.call_id) && (
                      <ProgramStatus $ok={programOutcomes.get(p.eventData?.call_id).status === 'completed'}>
                        {programOutcomes.get(p.eventData?.call_id).status}
                      </ProgramStatus>
                    )}
                  </JsonLabel>
                  <ProgramCode>{p.eventData?.code}</ProgramCode>
                  {toolOutputsByProgram.get(p.eventData?.call_id)?.map((t, j) => (
                    <ToolResult className="nested" key={t.eventData?.call_id || j}>
                      <ToolHeader>
                        <ToolResultIcon icon="check" color={colors.success} />
                        <ToolNameLabel>{t.eventData?.name}</ToolNameLabel>
                      </ToolHeader>
                      <CollapsibleJson data={t.eventData?.arguments} label="Arguments" />
                      <CollapsibleJson data={t.eventData?.result} label="Result" />
                    </ToolResult>
                  ))}
                  {programOutcomes.get(p.eventData?.call_id)?.result && (
                    <CollapsibleJson data={programOutcomes.get(p.eventData?.call_id).result} label="Returned" />
                  )}
                </div>
              ))}
            </>
          )}

          {directToolOutputs.length > 0 && (
            <>
              <SectionHeader>Tool Results</SectionHeader>
              {directToolOutputs.map((t, i) => (
                <ToolResult key={t.eventData?.call_id || i}>
                  <ToolHeader>
                    <ToolResultIcon icon="check" color={colors.success} />
                    <ToolNameLabel>{t.eventData?.name}</ToolNameLabel>
                    {t.eventData?.caller && <CallerTag>via program {t.eventData.caller.caller_id}</CallerTag>}
                  </ToolHeader>
                  <CollapsibleJson data={t.eventData?.arguments} label="Arguments" />
                  <CollapsibleJson data={t.eventData?.result} label="Result" />
                </ToolResult>
              ))}
            </>
          )}

          {span.toolErrors.length > 0 && (
            <>
              <SectionHeader>Tool Errors</SectionHeader>
              {span.toolErrors.map((t, i) => (
                <ToolResult key={t.eventData?.call_id || i}>
                  <ToolHeader>
                    <ToolResultIcon icon="xmark" color={colors.danger} />
                    <ToolNameLabel>{t.eventData?.name}</ToolNameLabel>
                    {t.eventData?.caller && <CallerTag>via program {t.eventData.caller.caller_id}</CallerTag>}
                  </ToolHeader>
                  <CollapsibleJson data={t.eventData?.arguments} label="Arguments" />
                  <ErrorText>{t.eventData?.error}</ErrorText>
                </ToolResult>
              ))}
            </>
          )}

          {span.recoveries.length > 0 && (
            <>
              <SectionHeader>Recoveries</SectionHeader>
              {span.recoveries.map((r, i) => (
                <RecoveryItem key={i}>
                  Missing tool output recovery: {r.eventData?.callId} ({r.eventData?.method})
                </RecoveryItem>
              ))}
            </>
          )}
        </Body>
      )}
    </Block>
  );
}
